import { BusinessObject } from './business-object.js'
import {
	jsonClass,
	jsonProperty,
	jsonArrayProperty,
	jsonMapProperty,
} from '@sumbricht/json-class-serializer'
import { ServerDataStoreEntry } from './server-data-store-entry.js'
import { RestrictableItem } from './restrictions/restrictable-item.js'
import { Parameter } from '../script/parameter.js'
import { BoVisitor } from './bo-visitor.js'
import { assignCommonProperties } from '../util/data/assign-common-properties.js'

import { ServerDataStoreProcedure } from './server-data-store-procedure.js'
import { ServerDataStoreView } from './server-data-store-view.js'
import { BoReference } from './bo-reference.js'
import { z } from 'zod'
import { Entity } from './entity.js'
import { EntitySubset } from './entity-subset.js'

export const ServerDataStoreTypes = {
	db: 'DataBase',
	file: 'File-based',
} as const

export const ServerDataStoreTypeSchema = z.enum(
	Object.keys(ServerDataStoreTypes) as [keyof typeof ServerDataStoreTypes],
)

export const ServerDataStoreDbTypes = {
	postgres: 'PostgreSQL',
	mssql: 'MS SQL',
	mysql: 'MySQL',
	sqlite: 'SQLite',
	oracle: 'Oracle',
} as const

@jsonClass('ServerDataStore')
export class ServerDataStore extends BusinessObject {
	@jsonProperty(String)
	storeType: keyof typeof ServerDataStoreTypes = 'db'
	@jsonProperty(Boolean)
	useSystemDatabase: boolean = true
	@jsonProperty(String)
	dbType!: keyof typeof ServerDataStoreDbTypes
	@jsonProperty(String)
	dbSchemaName: string = 'solution'
	@jsonProperty(BoReference)
	dbCredentialConfigurationItem?: BoReference

	@jsonProperty(Boolean)
	createStaticEntityTables: boolean = false

	@jsonArrayProperty(ServerDataStoreEntry)
	entries: ServerDataStoreEntry[] = []
	@jsonArrayProperty(ServerDataStoreProcedure)
	procedures: ServerDataStoreProcedure[] = []
	@jsonArrayProperty(ServerDataStoreView)
	views: ServerDataStoreView[] = []

	readonly affectsDb: boolean = true

	constructor(init?: Partial<ServerDataStore>) {
		super()
		assignCommonProperties(this, init)
	}

	getRestrictableItems(): RestrictableItem[] {
		if (this.storeType == 'db') {
			const restrictables: RestrictableItem[] = []
			for (const entry of this.entries) {
				switch (entry.boType) {
					case 'Entity':
						restrictables.push(
							new RestrictableItem({
								itemName: entry.getQualifiedName(),
								functionName: `access_${entry.moduleId}__${entry.boId}`,
								parameters: [
									new Parameter({
										name: 'operation',
										type: `'read' | 'create' | 'update' | 'delete'`,
									}),
									new Parameter({
										name: 'accessController',
										type: `SdsEntityAccessController<typeof ${entry.getQualifiedName()}>`,
									}),
								],
								contextImports: {
									SdsEntityAccessController: '@lg/shared',
								},
								defaultCode: `\taccessController.allowServer()\n\tif(Sys.User.currentUser?.hasEffectivePermission('Module.WhicheverPermissionIsRequired')) {\n\t\taccessController.allowServerAndClient()\n\t}\n\tswitch(operation) {\n\t\tcase 'read':\n\t\tcase 'create':\n\t\tcase 'update':\n\t\tcase 'delete':\n\t\t\t//accessController.allowServerAndClient()\n\t\t\tbreak\n\t}`,
							}),
						)
						break

					default:
					// ignore
				}
			}
			return restrictables
		}

		return []
	}

	includesEntry(entry: ServerDataStoreEntry): boolean {
		return this.entries.some((e) => e.matches(entry))
	}

	getClassNames(): string[] {
		return [this.getQualifiedName()]
	}

	visit(visitor: BoVisitor): void {
		this.entries.forEach((entry) =>
			visitor.visitBoReference(entry, ['entries']),
		)
	}

	static getDbTableNameForEntity(
		store: ServerDataStore | null,
		entity: Entity,
		subset: EntitySubset | undefined,
	) {
		if (store && store.storeType != 'db')
			throw new Error('Store is not a database')
		const subsetName = subset
			? `${entity.getQualifiedName()}.${subset.name}`
			: entity.getQualifiedName()

		return subsetName.replaceAll('.', '__')
	}
}
